// Admin/ScientificProgramAdmin.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "../../api/axiosInstance";
import App from "../../Utils/LayoutMobile";

/* ─── Design tokens ─────────────────────────────────────── */
const C_PINK   = "#F04DA0";
const C_PURPLE = "#8B2FC9";
const C_DARK   = "#1a0040";
const C_GRAD   = `linear-gradient(135deg, ${C_PINK}, ${C_PURPLE})`;

/* ─── Toast ─────────────────────────────────────────────── */
let _toastId = 0;
function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = (msg, type = "success") => {
    const id = ++_toastId;
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3500);
  };
  return {
    toasts,
    success: (m) => add(m, "success"),
    error:   (m) => add(m, "error"),
    warning: (m) => add(m, "warning"),
  };
}

/* ─── File Upload Field ──────────────────────────────────── */
function UploadField({ label, accept = "image/*", fileList, onChange, hint }) {
  const inputRef = useRef();
  const file = fileList?.[0];

  const handleFiles = (files) => {
    if (!files?.length) return;
    const f = files[0];
    onChange([{ uid: Date.now(), name: f.name, status: "done", originFileObj: f, url: URL.createObjectURL(f) }]);
  };

  return (
    <div className="sp-field">
      {label && <label className="sp-label">{label}</label>}
      <div
        className={`sp-upload-zone ${file ? "sp-upload-zone--has-file" : ""}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("sp-upload-zone--drag"); }}
        onDragLeave={(e) => e.currentTarget.classList.remove("sp-upload-zone--drag")}
        onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("sp-upload-zone--drag"); handleFiles(e.dataTransfer.files); }}
      >
        <input ref={inputRef} type="file" accept={accept} style={{ display: "none" }}
          onChange={(e) => handleFiles(e.target.files)} />
        {file ? (
          <div className="sp-upload-preview">
            {file.url && accept.startsWith("image") ? (
              <img src={file.url} alt="preview" className="sp-preview-img" />
            ) : (
              <div className="sp-file-icon">📄</div>
            )}
            <div className="sp-upload-meta">
              <span className="sp-upload-name">{file.name}</span>
              <span className="sp-upload-change">Click to change</span>
            </div>
            <button className="sp-upload-remove" onClick={(e) => { e.stopPropagation(); onChange([]); }}>
              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="sp-upload-placeholder">
            <div className="sp-upload-ico">
              <svg width="22" height="22" fill="none" stroke={C_PINK} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" />
                <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3" />
              </svg>
            </div>
            <span className="sp-upload-text">Drop file or <strong>click to browse</strong></span>
            {hint && <span className="sp-upload-hint">{hint}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Section Card ───────────────────────────────────────── */
function SectionCard({ title, subtitle, icon, iconColor, iconBg, delay = 0, children }) {
  return (
    <div className="sp-section-card" style={{ animationDelay: `${delay}s` }}>
      <div className="sp-section-header">
        <span className="sp-section-icon" style={{ color: iconColor, background: iconBg }}>{icon}</span>
        <div>
          <h2 className="sp-section-title">{title}</h2>
          {subtitle && <p className="sp-section-sub">{subtitle}</p>}
        </div>
      </div>
      <div className="sp-section-body">{children}</div>
    </div>
  );
}

/* ─── Stat Input ─────────────────────────────────────────── */
function StatInput({ label, value, onChange, placeholder }) {
  return (
    <div className="sp-field">
      <label className="sp-label">{label}</label>
      <input className="sp-input" type="text" value={value}
        onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════ */
const ScientificProgramAdmin = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);

  /* ── State ── */
  const [heroBg,       setHeroBg]       = useState([]);
  const [scientificBg, setScientificBg] = useState([]);
  const [agendaBg,     setAgendaBg]     = useState([]);
  const [doctorImg,    setDoctorImg]    = useState([]);

  const [aboutTitle,       setAboutTitle]       = useState("");
  const [aboutDescription, setAboutDescription] = useState("");
  const [highlights,  setHighlights]  = useState([{ id: Date.now(), bold: "", text: "" }]);
  const [stats,       setStats]       = useState([
    { id: 1, value: "40+", label: "SCIENTIFIC SESSIONS" },
    { id: 2, value: "60+", label: "GLOBAL SPEAKERS" },
  ]);

  const [agendaTitle,    setAgendaTitle]    = useState("Scientific Agenda");
  const [agendaSubtitle, setAgendaSubtitle] = useState("");

  /* ── Agenda Items state ── */
  const [agendaItems, setAgendaItems] = useState([
    { id: Date.now(), title: "Introduction to Dermatology" },
  ]);

  /* ── Fetch ── */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get("/api/v1/scientific-program");
        const d   = res.data?.data || {};

        setAboutTitle(d.aboutTitle || "ABOUT THE PROGRAM");
        setAboutDescription(d.aboutDescription || "");
        setHighlights(d.highlights?.length ? d.highlights.map((h, i) => ({ ...h, id: i })) : [{ id: 0, bold: "", text: "" }]);
        setStats(d.stats?.length ? d.stats.map((s, i) => ({ ...s, id: i })) : [
          { id: 0, value: "40+", label: "SCIENTIFIC SESSIONS" },
          { id: 1, value: "60+", label: "GLOBAL SPEAKERS" },
        ]);
        setAgendaTitle(d.agendaTitle || "Scientific Agenda");
        setAgendaSubtitle(d.agendaSubtitle || "");

        /* Agenda items from DB */
        if (d.agendaItems?.length) {
          setAgendaItems(d.agendaItems.map((item, i) => ({
            id: item._id || i,
            title: item.title || "",
          })));
        }

        if (d.heroBgUrl)       setHeroBg([{ uid: "-h", name: "hero-bg.jpg",       status: "done", url: d.heroBgUrl }]);
        if (d.scientificBgUrl) setScientificBg([{ uid: "-s", name: "scientific-bg.jpg", status: "done", url: d.scientificBgUrl }]);
        if (d.agendaBgUrl)     setAgendaBg([{ uid: "-a", name: "agenda-bg.jpg",   status: "done", url: d.agendaBgUrl }]);
        if (d.doctorImgUrl)    setDoctorImg([{ uid: "-d", name: "doctor.jpg",     status: "done", url: d.doctorImgUrl }]);
      } catch {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ── Highlight helpers ── */
  const addHighlight    = () => setHighlights((p) => [...p, { id: Date.now(), bold: "", text: "" }]);
  const removeHighlight = (id) => setHighlights((p) => p.filter((h) => h.id !== id));
  const updateHighlight = (id, key, val) => setHighlights((p) => p.map((h) => h.id === id ? { ...h, [key]: val } : h));

  /* ── Stat helpers ── */
  const addStat    = () => setStats((p) => [...p, { id: Date.now(), value: "", label: "" }]);
  const removeStat = (id) => setStats((p) => p.filter((s) => s.id !== id));
  const updateStat = (id, key, val) => setStats((p) => p.map((s) => s.id === id ? { ...s, [key]: val } : s));

  /* ── Agenda Item helpers ── */
  const addAgendaItem    = () => setAgendaItems((p) => [...p, { id: Date.now(), title: "" }]);
  const removeAgendaItem = (id) => setAgendaItems((p) => p.filter((a) => a.id !== id));
  const updateAgendaItem = (id, val) => setAgendaItems((p) => p.map((a) => a.id === id ? { ...a, title: val } : a));

  /* ── Move item up/down ── */
  const moveAgendaItem = (idx, dir) => {
    setAgendaItems((p) => {
      const arr  = [...p];
      const swap = idx + dir;
      if (swap < 0 || swap >= arr.length) return arr;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return arr;
    });
  };

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData();

    fd.append("aboutTitle",       aboutTitle);
    fd.append("aboutDescription", aboutDescription);
    fd.append("highlights",       JSON.stringify(highlights));
    fd.append("stats",            JSON.stringify(stats));
    fd.append("agendaTitle",      agendaTitle);
    fd.append("agendaSubtitle",   agendaSubtitle);
    fd.append("agendaItems",      JSON.stringify(agendaItems.map((a) => ({ title: a.title }))));

    const imgFields = { heroBg, scientificBg, agendaBg, doctorImg };
    Object.entries(imgFields).forEach(([key, fileList]) => {
      const file = fileList?.[0];
      if (file?.originFileObj) fd.append(key, file.originFileObj);
      else if (file?.url)      fd.append(`${key}Url`, file.url);
    });

    setSaving(true);
    try {
      await axios.post("/api/v1/scientific-program", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Scientific Program settings saved!");
    } catch (err) {
      toast.error(err?.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  /* ── Icons ── */
  const BgIcon = <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>;
  const TextIcon = <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>;
  const StatIcon = <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>;
  const AgendaIcon = <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M14.5 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="16" y2="17" /></svg>;
  const GridIcon = <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>;

  return (
    <App>
      <div className="sp-page">

        {/* ── Toasts ── */}
        <div className="sp-toast-container">
          {toast.toasts.map((t) => (
            <div key={t.id} className={`sp-toast sp-toast--${t.type}`}>
              {t.type === "success" && <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>}
              {t.type === "error"   && <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>}
              {t.type === "warning" && <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>}
              <span>{t.msg}</span>
            </div>
          ))}
        </div>

        {/* ── Page Header ── */}
        <div className="sp-page-header">
          <div className="sp-eyebrow"><span className="sp-eyebrow-dot" />Admin Panel</div>
          <h1 className="sp-page-title">Scientific Program <span className="sp-grad">Settings</span></h1>
          <p className="sp-page-sub">Manage backgrounds, about content, stats, agenda header and agenda cards.</p>
        </div>

        {loading && (
          <div className="sp-loading"><div className="sp-spinner" /><span>Loading content…</span></div>
        )}

        {!loading && (
          <form onSubmit={handleSubmit} className="sp-form">

            {/* ══ 1: BACKGROUNDS ══ */}
            <SectionCard title="Background Images" subtitle="Upload backgrounds for Hero, Scientific Program, and Agenda sections"
              icon={BgIcon} iconColor={C_PINK} iconBg="rgba(240,77,160,0.10)" delay={0.05}>
              <div className="sp-bg-preview-row">
                <div className="sp-bg-preview-item">
                  <span className="sp-badge" style={{ background: "rgba(139,47,201,0.1)", color: C_PURPLE }}>Doctor Image</span>
                  <UploadField label="Doctor / Speaker Image" hint="PNG or JPG, min 600×800px" fileList={doctorImg} onChange={setDoctorImg} />
                </div>
              </div>
              {doctorImg[0] && (
                <div className="sp-bg-previews-strip">
                  <p className="sp-preview-title">Live Preview</p>
                  <div className="sp-preview-thumbs">
                    
                    {doctorImg[0]    && <div className="sp-preview-thumb"><img src={doctorImg[0].url} alt="doctor" /><span>Doctor</span></div>}
                  </div>
                </div>
              )}
            </SectionCard>

            {/* ══ 2: ABOUT THE PROGRAM ══ */}
            <SectionCard title="About The Program" subtitle="Left-side text content in the Scientific Program section"
              icon={TextIcon} iconColor={C_PURPLE} iconBg="rgba(139,47,201,0.10)" delay={0.1}>
              <div className="sp-field">
                <label className="sp-label">Section Badge Title</label>
                <input className="sp-input" type="text" value={aboutTitle}
                  onChange={(e) => setAboutTitle(e.target.value)} placeholder="ABOUT THE PROGRAM" />
              </div>
              <div className="sp-field">
                <label className="sp-label">Program Description</label>
                <textarea className="sp-textarea" rows={5} value={aboutDescription}
                  onChange={(e) => setAboutDescription(e.target.value)}
                  placeholder="Skinola International Aesthetics presents a meticulously curated scientific program…" />
              </div>

              <div className="sp-divider" />
              <div className="sp-highlights-header">
                <div>
                  <p className="sp-sub-heading">Bullet Highlights</p>
                  <p className="sp-sub-desc">Appear as bullet points below the description.</p>
                </div>
                <button type="button" className="sp-add-btn" onClick={addHighlight}>
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                  Add Highlight
                </button>
              </div>
              <div className="sp-highlights-list">
                {highlights.map((h, idx) => (
                  <div key={h.id} className="sp-highlight-row">
                    <div className="sp-highlight-num">{idx + 1}</div>
                    <div className="sp-highlight-inputs">
                      <input className="sp-input" type="text" value={h.bold}
                        onChange={(e) => updateHighlight(h.id, "bold", e.target.value)}
                        placeholder="Bold text (e.g. Case-based discussions)" style={{ fontWeight: 700 }} />
                      <input className="sp-input" type="text" value={h.text}
                        onChange={(e) => updateHighlight(h.id, "text", e.target.value)}
                        placeholder="Regular text (e.g. bridging theory and clinical practice…)" />
                    </div>
                    {highlights.length > 1 && (
                      <button type="button" className="sp-remove-btn" onClick={() => removeHighlight(h.id)}>
                        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* ══ 3: STATS ══ */}
            <SectionCard title="Program At A Glance — Stats" subtitle="Statistics shown in the pink card (e.g. 40+ Scientific Sessions)"
              icon={StatIcon} iconColor={C_PINK} iconBg="rgba(240,77,160,0.10)" delay={0.15}>
              <div className="sp-stats-list">
                {stats.map((s, idx) => (
                  <div key={s.id} className="sp-stat-row">
                    <div className="sp-stat-order">#{idx + 1}</div>
                    <div className="sp-grid-2 sp-stat-inputs">
                      <StatInput label="Value" value={s.value} onChange={(v) => updateStat(s.id, "value", v)} placeholder="e.g. 40+" />
                      <StatInput label="Label" value={s.label} onChange={(v) => updateStat(s.id, "label", v)} placeholder="e.g. SCIENTIFIC SESSIONS" />
                    </div>
                    {stats.length > 1 && (
                      <button type="button" className="sp-remove-btn" onClick={() => removeStat(s.id)}>
                        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" className="sp-add-btn sp-add-btn--full" onClick={addStat}>
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                Add Stat
              </button>
            </SectionCard>

            {/* ══ 4: AGENDA SECTION HEADER ══ */}
            <SectionCard title="Scientific Agenda — Header" subtitle="Title and subtitle shown above the agenda grid"
              icon={AgendaIcon} iconColor={C_PURPLE} iconBg="rgba(139,47,201,0.10)" delay={0.2}>
              <div className="sp-grid-2">
                <div className="sp-field">
                  <label className="sp-label">Agenda Title</label>
                  <input className="sp-input" type="text" value={agendaTitle}
                    onChange={(e) => setAgendaTitle(e.target.value)} placeholder="e.g. Scientific Agenda" />
                  <div className="sp-title-hint">💡 First word = Dark, rest = Pink gradient</div>
                </div>
                <div className="sp-field">
                  <label className="sp-label">Subtitle / Description</label>
                  <textarea className="sp-textarea" rows={3} value={agendaSubtitle}
                    onChange={(e) => setAgendaSubtitle(e.target.value)}
                    placeholder="Explore a comprehensive curriculum covering the latest advancements…" />
                </div>
              </div>
            </SectionCard>

            {/* ══ 5: AGENDA ITEMS (numbered cards) ══ */}
            <SectionCard
              title="Scientific Agenda — Cards"
              subtitle={`Manage the numbered topic cards shown in the 3-column grid (${agendaItems.length} items)`}
              icon={GridIcon} iconColor={C_PINK} iconBg="rgba(240,77,160,0.10)" delay={0.25}
            >
              {/* Mini preview of the grid */}
              {/* {agendaItems.length > 0 && (
                <div className="sp-agenda-mini-preview">
                  {agendaItems.map((item, idx) => (
                    <div key={item.id} className="sp-agenda-mini-card">
                      <span className="sp-agenda-mini-num">{String(idx + 1).padStart(2, "0")}</span>
                      <span className="sp-agenda-mini-title">{item.title || <em style={{ color: "#b4a4c8" }}>Untitled</em>}</span>
                    </div>
                  ))}
                </div>
              )} */}

              <div className="sp-divider" />

              {/* Editable list */}
              <div className="sp-agenda-items-header">
                <div>
                  <p className="sp-sub-heading">Edit Cards</p>
                  <p className="sp-sub-desc">Use arrows to reorder. Numbers auto-update on frontend.</p>
                </div>
                <button type="button" className="sp-add-btn" onClick={addAgendaItem}>
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                  Add Card
                </button>
              </div>

              <div className="sp-agenda-edit-list">
                {agendaItems.map((item, idx) => (
                  <div key={item.id} className="sp-agenda-edit-row">
                    {/* Number badge */}
                    <div className="sp-agenda-edit-num"
                      style={{ background: C_GRAD }}>
                      {String(idx + 1).padStart(2, "0")}
                    </div>

                    {/* Title input */}
                    <input
                      className="sp-input sp-agenda-edit-input"
                      type="text"
                      value={item.title}
                      onChange={(e) => updateAgendaItem(item.id, e.target.value)}
                      placeholder="e.g. Introduction to Dermatology"
                    />

                    {/* Up / Down / Delete */}
                    <div className="sp-agenda-edit-actions">
                      <button type="button" className="sp-order-btn"
                        onClick={() => moveAgendaItem(idx, -1)} disabled={idx === 0}
                        title="Move up">
                        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="18 15 12 9 6 15" /></svg>
                      </button>
                      <button type="button" className="sp-order-btn"
                        onClick={() => moveAgendaItem(idx, 1)} disabled={idx === agendaItems.length - 1}
                        title="Move down">
                        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9" /></svg>
                      </button>
                      {agendaItems.length > 1 && (
                        <button type="button" className="sp-remove-btn" onClick={() => removeAgendaItem(item.id)} title="Delete">
                          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="sp-agenda-count-info">
                <svg width="14" height="14" fill="none" stroke={C_PURPLE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>Total <strong>{agendaItems.length}</strong> agenda cards. Frontend displays them in a 3-column grid numbered automatically.</span>
              </div>
            </SectionCard>

            {/* ══ SUBMIT BAR ══ */}
            <div className="sp-submit-bar">
              <div className="sp-submit-info">
                <svg width="14" height="14" fill="none" stroke="#9a86b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                Changes reflect live on the Scientific Program page immediately.
              </div>
              <button type="submit" className="sp-save-btn" disabled={saving}>
                {saving ? (
                  <><span className="sp-btn-spinner" /><span>Saving…</span></>
                ) : (
                  <>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                      <polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
                    </svg>
                    <span>Save All Changes</span>
                  </>
                )}
              </button>
            </div>

          </form>
        )}
      </div>

      <style>{`
        @keyframes sp-fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes sp-spin   { to{transform:rotate(360deg)} }
        @keyframes sp-pulse  { 0%,100%{opacity:1} 50%{opacity:.35} }
        @keyframes sp-toast-in { from{opacity:0;transform:translateX(32px)} to{opacity:1;transform:translateX(0)} }
        *,*::before,*::after{box-sizing:border-box}

        .sp-page{background:#f4eefa;min-height:100vh;padding:28px 32px 72px;display:flex;flex-direction:column;gap:24px;font-family:inherit;position:relative;}

        .sp-page-header{animation:sp-fadeUp .4s ease both;}
        .sp-eyebrow{display:inline-flex;align-items:center;gap:7px;font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:${C_PINK};background:rgba(240,77,160,0.08);border:1px solid rgba(240,77,160,0.2);padding:5px 14px 5px 10px;border-radius:50px;margin-bottom:12px;}
        .sp-eyebrow-dot{width:6px;height:6px;border-radius:50%;background:${C_PINK};animation:sp-pulse 2s infinite;flex-shrink:0;}
        .sp-page-title{font-size:clamp(22px,2.5vw,30px);font-weight:800;color:${C_DARK};letter-spacing:-.025em;margin:0 0 6px;}
        .sp-grad{background:linear-gradient(90deg,${C_PINK},#c066f0);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
        .sp-page-sub{font-size:13.5px;color:#8a7a9a;margin:0;}

        .sp-loading{display:flex;align-items:center;gap:12px;padding:48px;justify-content:center;color:#8a7a9a;font-size:14px;font-weight:600;}
        .sp-spinner{width:28px;height:28px;border-radius:50%;border:3px solid rgba(240,77,160,0.15);border-top-color:${C_PINK};animation:sp-spin .75s linear infinite;}

        .sp-form{display:flex;flex-direction:column;gap:20px;}
        .sp-section-card{background:#fff;border-radius:20px;border:1px solid #ede5f5;box-shadow:0 4px 20px rgba(26,0,64,.06);overflow:hidden;animation:sp-fadeUp .4s ease both;}
        .sp-section-header{display:flex;align-items:center;gap:14px;padding:20px 26px 18px;border-bottom:1px solid #f3ecfa;background:#faf6fe;}
        .sp-section-icon{width:44px;height:44px;border-radius:12px;flex-shrink:0;display:flex;align-items:center;justify-content:center;}
        .sp-section-title{font-size:16px;font-weight:800;color:${C_DARK};margin:0 0 2px;}
        .sp-section-sub{font-size:12px;color:#9a86b8;margin:0;}
        .sp-section-body{padding:24px 26px 28px;display:flex;flex-direction:column;gap:20px;}

        .sp-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:20px;}
        .sp-field{display:flex;flex-direction:column;gap:7px;}
        .sp-label{font-size:12px;font-weight:700;color:#5a5070;letter-spacing:.03em;}
        .sp-input{height:44px;border-radius:11px;border:1.5px solid #e8d8f8;padding:0 14px;font-size:13.5px;color:${C_DARK};background:#fdfbff;transition:border-color .2s,box-shadow .2s;outline:none;font-family:inherit;width:100%;}
        .sp-input:focus{border-color:${C_PINK};box-shadow:0 0 0 3px rgba(240,77,160,0.12);background:#fff;}
        .sp-input::placeholder{color:#c4b4d8;font-size:13px;}
        .sp-textarea{border-radius:11px;border:1.5px solid #e8d8f8;padding:12px 14px;font-size:13.5px;color:${C_DARK};background:#fdfbff;transition:border-color .2s,box-shadow .2s;outline:none;font-family:inherit;width:100%;resize:vertical;line-height:1.6;}
        .sp-textarea:focus{border-color:${C_PINK};box-shadow:0 0 0 3px rgba(240,77,160,0.12);background:#fff;}
        .sp-textarea::placeholder{color:#c4b4d8;font-size:13px;}

        /* Upload */
        .sp-upload-zone{border:1.5px dashed #d8c8f0;border-radius:12px;background:#fdfbff;padding:18px 16px;cursor:pointer;transition:border-color .2s,background .2s,box-shadow .2s;min-height:80px;display:flex;align-items:center;}
        .sp-upload-zone:hover,.sp-upload-zone--drag{border-color:${C_PINK};background:rgba(240,77,160,0.03);box-shadow:0 0 0 3px rgba(240,77,160,0.1);}
        .sp-upload-zone--has-file{border-style:solid;border-color:#e8d8f8;background:#fff;}
        .sp-upload-placeholder{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;width:100%;text-align:center;}
        .sp-upload-ico{width:42px;height:42px;border-radius:10px;background:rgba(240,77,160,0.08);display:flex;align-items:center;justify-content:center;}
        .sp-upload-text{font-size:12.5px;color:#8a7a9a;}.sp-upload-text strong{color:${C_PINK};}
        .sp-upload-hint{font-size:11px;color:#b4a4c8;}
        .sp-upload-preview{display:flex;align-items:center;gap:14px;width:100%;}
        .sp-preview-img{width:52px;height:52px;object-fit:cover;border-radius:8px;border:1px solid #ede5f5;flex-shrink:0;}
        .sp-upload-meta{flex:1;min-width:0;}
        .sp-upload-name{display:block;font-size:13px;font-weight:700;color:${C_DARK};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
        .sp-upload-change{font-size:11px;color:#9a86b8;margin-top:2px;display:block;}
        .sp-upload-remove{width:28px;height:28px;border-radius:7px;flex-shrink:0;border:1.5px solid #ffd0ea;background:#fff0f8;display:flex;align-items:center;justify-content:center;cursor:pointer;color:${C_PINK};transition:background .2s;}
        .sp-upload-remove:hover{background:#ffe0f2;}

        .sp-bg-preview-row{display:grid;grid-template-columns:1fr 1fr;gap:20px;}
        .sp-bg-preview-item{display:flex;flex-direction:column;gap:8px;}
        .sp-badge{display:inline-block;font-size:11px;font-weight:700;padding:3px 10px;border-radius:20px;letter-spacing:.05em;}
        .sp-bg-previews-strip{background:#faf6fe;border:1px solid #ede5f5;border-radius:12px;padding:16px 20px;}
        .sp-preview-title{font-size:12px;font-weight:700;color:#9a86b8;margin:0 0 12px;}
        .sp-preview-thumbs{display:flex;gap:12px;}
        .sp-preview-thumb{display:flex;flex-direction:column;align-items:center;gap:6px;}
        .sp-preview-thumb img{width:120px;height:70px;object-fit:cover;border-radius:8px;border:1px solid #ede5f5;}
        .sp-preview-thumb span{font-size:11px;font-weight:600;color:#8a7a9a;}

        .sp-divider{height:1px;background:#f0e8fa;margin:4px 0;}

        .sp-highlights-header,.sp-agenda-items-header{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;}
        .sp-sub-heading{font-size:14px;font-weight:700;color:${C_DARK};margin:0 0 4px;}
        .sp-sub-desc{font-size:12px;color:#9a86b8;margin:0;}
        .sp-highlights-list{display:flex;flex-direction:column;gap:12px;}
        .sp-highlight-row{display:flex;align-items:flex-start;gap:12px;background:#faf6fe;border:1px solid #ede5f5;border-radius:12px;padding:14px;}
        .sp-highlight-num{width:28px;height:28px;border-radius:8px;background:${C_GRAD};color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;flex-shrink:0;margin-top:8px;}
        .sp-highlight-inputs{flex:1;display:flex;flex-direction:column;gap:8px;}
        .sp-remove-btn{width:30px;height:30px;border-radius:8px;flex-shrink:0;border:1.5px solid #ffd0ea;background:#fff0f8;display:flex;align-items:center;justify-content:center;cursor:pointer;color:${C_PINK};margin-top:7px;transition:background .2s;}
        .sp-remove-btn:hover{background:#ffe0f2;}

        .sp-add-btn{display:inline-flex;align-items:center;gap:7px;padding:9px 18px;border-radius:10px;border:1.5px solid rgba(240,77,160,0.3);background:rgba(240,77,160,0.06);color:${C_PINK};font-size:13px;font-weight:700;cursor:pointer;transition:background .2s,border-color .2s;font-family:inherit;white-space:nowrap;}
        .sp-add-btn:hover{background:rgba(240,77,160,0.12);border-color:${C_PINK};}
        .sp-add-btn--full{width:100%;justify-content:center;}

        .sp-stats-list{display:flex;flex-direction:column;gap:12px;}
        .sp-stat-row{display:flex;align-items:flex-start;gap:12px;background:#faf6fe;border:1px solid #ede5f5;border-radius:12px;padding:14px;}
        .sp-stat-order{width:28px;height:28px;border-radius:8px;background:rgba(139,47,201,0.1);color:${C_PURPLE};display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;flex-shrink:0;margin-top:22px;}
        .sp-stat-inputs{flex:1;}

        .sp-title-hint{font-size:11.5px;color:#9a86b8;background:rgba(240,77,160,0.05);border:1px solid rgba(240,77,160,0.15);border-radius:8px;padding:8px 12px;margin-top:4px;}

        /* ── Agenda Mini Preview ── */
        .sp-agenda-mini-preview{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;}
        .sp-agenda-mini-card{display:flex;align-items:center;gap:10px;background:#1a0040;border-radius:10px;padding:10px 14px;}
        .sp-agenda-mini-num{font-size:11px;font-weight:800;color:#F04DA0;background:rgba(240,77,160,0.15);width:28px;height:28px;border-radius:6px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
        .sp-agenda-mini-title{font-size:11.5px;font-weight:600;color:#fff;line-height:1.3;}

        /* ── Agenda Edit List ── */
        .sp-agenda-edit-list{display:flex;flex-direction:column;gap:10px;}
        .sp-agenda-edit-row{display:flex;align-items:center;gap:10px;background:#faf6fe;border:1px solid #ede5f5;border-radius:12px;padding:10px 14px;}
        .sp-agenda-edit-num{width:34px;height:34px;border-radius:8px;color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;flex-shrink:0;}
        .sp-agenda-edit-input{flex:1;margin:0;}
        .sp-agenda-edit-actions{display:flex;align-items:center;gap:6px;flex-shrink:0;}
        .sp-order-btn{width:28px;height:28px;border-radius:7px;border:1.5px solid #e8d8f8;background:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#8a7a9a;transition:border-color .2s,color .2s;}
        .sp-order-btn:hover:not(:disabled){border-color:${C_PURPLE};color:${C_PURPLE};}
        .sp-order-btn:disabled{opacity:.3;cursor:not-allowed;}
        .sp-agenda-edit-row .sp-remove-btn{margin-top:0;}

        .sp-agenda-count-info{display:flex;align-items:center;gap:8px;background:rgba(139,47,201,0.05);border:1px solid rgba(139,47,201,0.15);border-radius:10px;padding:12px 16px;font-size:12.5px;color:#6a5a7a;}
        .sp-agenda-count-info strong{color:${C_PURPLE};}

        /* Submit */
        .sp-submit-bar{display:flex;align-items:center;justify-content:space-between;background:#fff;border-radius:16px;border:1px solid #ede5f5;box-shadow:0 4px 20px rgba(26,0,64,.06);padding:18px 26px;animation:sp-fadeUp .4s ease .2s both;gap:16px;flex-wrap:wrap;}
        .sp-submit-info{display:flex;align-items:center;gap:8px;font-size:12.5px;color:#9a86b8;font-weight:500;}
        .sp-save-btn{display:inline-flex;align-items:center;gap:10px;padding:13px 32px;background:${C_GRAD};color:#fff;font-size:14px;font-weight:700;border:none;border-radius:12px;cursor:pointer;box-shadow:0 8px 24px rgba(240,77,160,0.3);transition:transform .25s cubic-bezier(.16,1,.3,1),box-shadow .25s,opacity .2s;letter-spacing:.02em;font-family:inherit;white-space:nowrap;}
        .sp-save-btn:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 14px 32px rgba(240,77,160,0.42);}
        .sp-save-btn:disabled{opacity:.65;cursor:not-allowed;}
        .sp-btn-spinner{width:16px;height:16px;border-radius:50%;border:2px solid rgba(255,255,255,0.3);border-top-color:#fff;animation:sp-spin .7s linear infinite;}

        /* Toasts */
        .sp-toast-container{position:fixed;top:20px;right:20px;z-index:9999;display:flex;flex-direction:column;gap:8px;pointer-events:none;}
        .sp-toast{display:inline-flex;align-items:center;gap:9px;padding:12px 18px;border-radius:12px;font-size:13px;font-weight:600;box-shadow:0 8px 24px rgba(26,0,64,.15);animation:sp-toast-in .35s cubic-bezier(.16,1,.3,1) both;max-width:340px;}
        .sp-toast--success{background:#f0fdf4;color:#16a34a;border:1px solid #bbf7d0;}
        .sp-toast--error{background:#fef2f2;color:#dc2626;border:1px solid #fecaca;}
        .sp-toast--warning{background:#fffbeb;color:#b45309;border:1px solid #fde68a;}

        @media(max-width:768px){
          .sp-page{padding:16px 16px 52px;gap:16px;}
          .sp-grid-2,.sp-bg-preview-row,.sp-agenda-mini-preview{grid-template-columns:1fr;}
          .sp-submit-bar{flex-direction:column;align-items:stretch;}
          .sp-save-btn{justify-content:center;}
          .sp-section-body{padding:18px 18px 22px;}
        }
      `}</style>
    </App>
  );
};

export default ScientificProgramAdmin;
